import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BASE_URL } from '../url';
import LoadingSpinner from './LoadingSpinner';
import SiteLayout from './SiteLayout';
import { CONTACT, IMAGES, causes, impactStats } from './siteData';
import { BiCalendarAlt, BiCheckShield, BiHeart, BiSolidEnvelope, BiSolidMap, BiSolidPhone } from 'react-icons/bi';

function driveImageUrl(imageURL) {
  if (!imageURL) return '';
  const pathArray = imageURL.split('/');
  return pathArray[5] ? `https://drive.google.com/uc?id=${pathArray[5]}` : imageURL;
}

function SectionHeader({ eyebrow, title, children, align = 'center' }) {
  return (
    <div className={`section-header text-${align}`}>
      <span className="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      {children && <p>{children}</p>}
    </div>
  );
}

function HeroSection() {
  return (
    <section id="home" className="home-hero">
      <div className="container">
        <div className="row align-items-center g-5">
          <div className="col-lg-6 reveal-up">
            <span className="hero-kicker">Community charity from Udupi</span>
            <h1>Small acts of kindness, raised into real smiles.</h1>
            <p className="hero-copy">
              Raising Smiles brings people together to support medical needs, education, orphanages, and urgent social causes with practical help and human care.
            </p>
            <div className="hero-actions">
              <a className="btn btn-primary-rs" href={CONTACT.donate} target="_blank" rel="noreferrer">Donate Now</a>
              <a className="btn btn-outline-rs" href="#impact">See Our Impact</a>
            </div>
            <div className="hero-trust">
              <span><BiCheckShield aria-hidden="true" /> Secure WhatsApp donation assistance</span>
              <span><BiHeart aria-hidden="true" /> Volunteer-led since 2015</span>
            </div>
          </div>
          <div className="col-lg-6 reveal-up">
            <div className="hero-image-wrap">
              <img src={IMAGES.hero} alt="Raising Smiles volunteers supporting the community" />
              <div className="hero-impact-card">
                <strong>Every contribution matters</strong>
                <span>Food, education, care, and urgent relief reach people faster when the community acts together.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ImpactSection() {
  return (
    <section id="impact" className="section-padding impact-section">
      <div className="container">
        <SectionHeader eyebrow="Our impact" title="Built by people who keep showing up">
          Since 2015, Raising Smiles has focused on direct, need-based support through donors, volunteers, and well-wishers.
        </SectionHeader>
        <div className="impact-grid">
          {impactStats.map((stat) => (
            <div className="impact-card reveal-up" key={stat.label}>
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section className="section-padding story-section" id="about">
      <div className="container">
        <div className="row align-items-center g-5">
          <div className="col-lg-6">
            <div className="story-image">
              <img src={IMAGES.about} alt="Raising Smiles community service activity" loading="lazy" />
            </div>
          </div>
          <div className="col-lg-6">
            <span className="eyebrow">Our story</span>
            <h2>Four young people started a circle of care. The circle kept growing.</h2>
            <p>
              Raising Smiles began on 21 December 2015 in Udupi, Karnataka, when four youths joined hands with a shared purpose: to serve humanity. Today, the voluntary humanitarian association supports poor families, education needs, medical relief, orphanages, and public charitable causes across communities.
            </p>
            <div className="mission-note">
              <strong>Our mission</strong>
              <span>To respond creatively to current and emerging needs, offering financial and moral support where it can create meaningful change.</span>
            </div>
            <a className="btn btn-outline-rs" href="#causes">Explore Causes</a>
          </div>
        </div>
      </div>
    </section>
  );
}

function CausesSection() {
  return (
    <section className="section-padding causes-section" id="causes">
      <div className="container">
        <SectionHeader eyebrow="Programs" title="Focused causes, personal support">
          Each program is grounded in practical help: a bill paid, a meal delivered, a student supported, a family heard.
        </SectionHeader>
        <div className="row g-4">
          {causes.map((cause) => (
            <div className="col-lg-3 col-md-6" key={cause.title}>
              <article className="cause-card reveal-up">
                <img src={cause.image} alt={`${cause.title} by Raising Smiles`} loading="lazy" />
                <div className="cause-card-body">
                  <span>{cause.category}</span>
                  <h3>{cause.title}</h3>
                  <p>{cause.description}</p>
                  <small>{cause.impact}</small>
                  <a href={CONTACT.donate} target="_blank" rel="noreferrer">Support this cause</a>
                </div>
              </article>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DonationSection() {
  const amounts = ['Rs 500', 'Rs 1,000', 'Rs 2,500', 'Any amount'];
  return (
    <section id="donor" className="section-padding donation-panel-section">
      <div className="container">
        <div className="donation-panel">
          <div>
            <span className="eyebrow">Donate with confidence</span>
            <h2>Your help can become medicine, school support, meals, or urgent relief.</h2>
            <p>Start a WhatsApp conversation with the team and choose how you would like to contribute. We keep the process simple, personal, and transparent.</p>
            <div className="donation-amounts" aria-label="Suggested donation amounts">
              {amounts.map((amount) => <span key={amount}>{amount}</span>)}
            </div>
          </div>
          <div className="donation-action-card">
            <BiCheckShield aria-hidden="true" />
            <h3>Simple and trusted giving</h3>
            <p>Secure donation guidance, direct team contact, and clear cause-led conversations.</p>
            <a className="btn btn-primary-rs w-100" href={CONTACT.donate} target="_blank" rel="noreferrer">Donate Now</a>
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="section-padding testimonial-modern">
      <div className="container">
        <SectionHeader eyebrow="Community voice" title="Care that feels close to home" />
        <div className="testimonial-card">
          <p>"Together we make a difference."</p>
          <div>
            <strong>Raising Smiles supporters</strong>
            <span>Donors, volunteers, and well-wishers contributing to community events</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function LatestNewsSection({ news, isLoading }) {
  const latestNews = useMemo(() => (news || []).slice(0, 3), [news]);

  return (
    <section className="section-padding latest-news-section" id="latest-news">
      <div className="container">
        <SectionHeader eyebrow="Latest updates" title="Recent moments from the field">
          Follow the events, donations, and community work made possible by supporters.
        </SectionHeader>
        {isLoading && <LoadingSpinner compact />}
        {!isLoading && latestNews.length === 0 && (
          <div className="empty-state">
            <h3>No updates yet</h3>
            <p>New stories from Raising Smiles will appear here once they are published.</p>
          </div>
        )}
        {!isLoading && latestNews.length > 0 && (
          <div className="row g-4">
            {latestNews.map((newsData) => (
              <div className="col-lg-4 col-md-6" key={newsData._id || newsData.postHeading}>
                <article className="news-card">
                  <Link to="/news">
                    <img src={driveImageUrl(newsData.imageURL)} className="news-card-image" alt={newsData.postHeading} loading="lazy" />
                  </Link>
                  <div className="news-card-body">
                    <span><BiCalendarAlt aria-hidden="true" /> Latest news</span>
                    <h3><Link to="/news">{newsData.postHeading}</Link></h3>
                    <p>{newsData.postDetails.length > 180 ? `${newsData.postDetails.substring(0, 180)}...` : newsData.postDetails}</p>
                  </div>
                </article>
              </div>
            ))}
          </div>
        )}
        <div className="text-center mt-5">
          <Link className="btn btn-outline-rs" to="/news">View More News</Link>
        </div>
      </div>
    </section>
  );
}

function VolunteerSection() {
  return (
    <section className="section-padding volunteer-modern" id="volunteer">
      <div className="container">
        <div className="row g-5 align-items-center">
          <div className="col-lg-6">
            <form className="custom-form modern-form" action="https://formsubmit.co/arnold4all@gmail.com" method="post">
              <span className="eyebrow">Volunteer</span>
              <h2>Bring your time, skills, and heart.</h2>
              <div className="row">
                <div className="col-md-6">
                  <label htmlFor="volunteer-name">Name</label>
                  <input type="text" name="volunteer-name" id="volunteer-name" className="form-control" placeholder="Your name" required />
                </div>
                <div className="col-md-6">
                  <label htmlFor="volunteer-email">Email</label>
                  <input type="email" name="volunteer-email" id="volunteer-email" className="form-control" placeholder="you@example.com" required />
                </div>
                <div className="col-12">
                  <label htmlFor="volunteer-phone">Phone</label>
                  <input type="tel" name="volunteer-subject" id="volunteer-phone" className="form-control" placeholder="Your phone number" required />
                </div>
              </div>
              <label htmlFor="volunteer-message">Message</label>
              <textarea name="volunteer-message" rows="4" className="form-control" id="volunteer-message" placeholder="Tell us how you would like to help"></textarea>
              <button type="submit" className="form-control">Submit Volunteer Interest</button>
            </form>
          </div>
          <div className="col-lg-6">
            <div className="volunteer-story">
              <img src={IMAGES.volunteer} alt="Volunteer helping through Raising Smiles" loading="lazy" />
              <h3>About volunteering</h3>
              <p>Your skills and compassion can help Raising Smiles respond faster, organize better, and reach more people with care.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BloodDonationSection() {
  return (
    <section className="blood-cta">
      <div className="container">
        <div className="blood-cta-inner">
          <span className="eyebrow">Lifesaving network</span>
          <h2>Join our Blood Donation WhatsApp Group</h2>
          <p>Be part of a responsive local network where a timely message can help someone find blood support when it matters most.</p>
          <a className="btn btn-light" href={CONTACT.bloodDonation} target="_blank" rel="noreferrer">Join Now</a>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section className="section-padding contact-modern" id="contact">
      <div className="container">
        <div className="row g-5">
          <div className="col-lg-5">
            <span className="eyebrow">Contact</span>
            <h2>Have a cause, question, or idea for change?</h2>
            <p>Reach the team directly or send a message. Donation, volunteer, and support conversations are kept simple and personal.</p>
            <ul className="contact-list">
              <li><BiSolidMap aria-hidden="true" /> {CONTACT.location}</li>
              <li><BiSolidPhone aria-hidden="true" /> <a href={CONTACT.phoneHref}>{CONTACT.phone}</a></li>
              <li><BiSolidEnvelope aria-hidden="true" /> <a href={CONTACT.emailHref}>{CONTACT.email}</a></li>
            </ul>
          </div>
          <div className="col-lg-7">
            <form className="custom-form contact-form modern-form" action="https://formsubmit.co/arnold4all@gmail.com" method="post">
              <div className="row">
                <div className="col-md-6">
                  <label htmlFor="first-name">First name</label>
                  <input type="text" name="first-name" id="first-name" className="form-control" placeholder="First name" required />
                </div>
                <div className="col-md-6">
                  <label htmlFor="last-name">Last name</label>
                  <input type="text" name="last-name" id="last-name" className="form-control" placeholder="Last name" required />
                </div>
              </div>
              <label htmlFor="email">Email</label>
              <input type="email" name="email" id="email" className="form-control" placeholder="you@example.com" required />
              <label htmlFor="phone">Phone</label>
              <input type="tel" name="phone" id="phone" className="form-control" placeholder="Your phone number" required />
              <label htmlFor="message">Message</label>
              <textarea name="message" rows="5" className="form-control" id="message" placeholder="How can we assist you?"></textarea>
              <button type="submit" className="form-control">Send Message</button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  const [viewAllNews, setViewAllNews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch(`${BASE_URL}/viewAllNews`)
      .then((response) => response.json())
      .then((data) => setViewAllNews(Array.isArray(data) ? data : []))
      .catch(() => setViewAllNews([]))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <SiteLayout>
      <main>
        <HeroSection />
        <ImpactSection />
        <AboutSection />
        <CausesSection />
        <DonationSection />
        <TestimonialsSection />
        <LatestNewsSection news={viewAllNews} isLoading={isLoading} />
        <VolunteerSection />
        <BloodDonationSection />
        <ContactSection />
      </main>
    </SiteLayout>
  );
}

export default HomePage;
